/**
 * SkillsPage — /agents/skills
 * The skill library on the ListPage template. Demo page: /tables/data-tables (toolbar with search and a scoped select
 * above one table).
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { BookOpenCheck } from 'lucide-react';
import { listSkillsOptions } from '../../generated/api/@tanstack/react-query.gen.js';
import type { SkillSummary } from '../../generated/api/types.gen.js';
import {
  Badge,
  Callout,
  DataTable,
  EmptyState,
  SearchBar,
  Toolbar,
  describeQueryError,
  type DataTableColumn,
} from '../../components/ui/index.js';
import { ListPage } from '../../components/page/index.js';
import { FilterSelect, facetOptions, sentenceCase } from '../../components/ui/FilterSelect.js';

const COLUMNS: DataTableColumn<SkillSummary>[] = [
  {
    key: 'name',
    header: 'Skill',
    width: '36%',
    sortable: true,
    sortValue: (skill) => skill.name.toLowerCase(),
    render: (skill) => (
      <div className="min-w-0">
        <Link
          to={`/agents/skills/${skill.slug}`}
          className={`text-[13px] font-semibold transition-colors hover:text-accent-text ${skill.enabled ? 'text-heading' : 'text-muted line-through'}`}
        >
          {skill.name}
        </Link>
        <p className="mt-0.5 line-clamp-1 max-w-[460px] text-[11px] text-muted">
          {skill.description || 'No description'}
        </p>
      </div>
    ),
  },
  {
    key: 'category',
    header: 'Category',
    sortable: true,
    sortValue: (skill) => skill.category,
    render: (skill) => <Badge variant="neutral">{sentenceCase(skill.category)}</Badge>,
  },
  {
    key: 'state',
    header: 'Version',
    sortable: true,
    sortValue: (skill) => skill.versionState,
    render: (skill) => (
      <span className="flex items-center gap-2">
        <span className="text-muted tabular-nums">v{skill.version}</span>
        <Badge variant={skill.versionState === 'locked' ? 'success' : 'warning'}>
          {sentenceCase(skill.versionState)}
        </Badge>
      </span>
    ),
  },
  {
    key: 'priority',
    header: 'Priority',
    align: 'right',
    sortable: true,
    sortValue: (skill) => skill.priority,
    render: (skill) => <span className="text-body tabular-nums">{skill.priority}</span>,
  },
  {
    key: 'status',
    header: 'Status',
    render: (skill) => (
      <Badge variant={skill.enabled ? 'success' : 'danger'} dot>
        {skill.enabled ? 'Enabled' : 'Disabled'}
      </Badge>
    ),
  },
  {
    key: 'usedBy',
    header: 'Used by',
    align: 'right',
    sortable: true,
    sortValue: (skill) => skill.usedByCount,
    render: (skill) =>
      skill.usedByCount === 0 ? (
        <span className="text-faint">None</span>
      ) : (
        <span className="text-body tabular-nums">{skill.usedByCount}</span>
      ),
  },
];

export const SkillsPage: React.FC = () => {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const query = useQuery(listSkillsOptions({ query: { limit: 500 } }));
  const skills = query.data?.data ?? [];

  const needle = search.trim().toLowerCase();
  const visible = skills.filter((skill) => {
    if (category && skill.category !== category) return false;
    return (
      !needle ||
      `${skill.name} ${skill.slug} ${skill.description ?? ''} ${skill.category}`.toLowerCase().includes(needle)
    );
  });

  return (
    <ListPage
      title="Skills"
      subtitle="Procedural memory loaded into agents when a template, daemon or workflow step needs it."
      breadcrumbs={[{ label: 'Libraries', to: '/agents/libraries' }, { label: 'Skills' }]}
      alert={
        query.isError ? (
          <Callout tone="danger" title="Could not load skills">
            {describeQueryError(query.error, 'skills')}
          </Callout>
        ) : undefined
      }
      toolbar={
        <Toolbar
          search={<SearchBar value={search} onChange={setSearch} placeholder="Search name, purpose, category…" />}
          right={
            <FilterSelect
              label="Category"
              anyLabel="All categories"
              value={category}
              options={facetOptions(skills, (skill) => skill.category)}
              onChange={setCategory}
            />
          }
        />
      }
    >
      {!query.isError && (
        <DataTable
          aria-label="Skills"
          columns={COLUMNS}
          rows={visible}
          rowKey={(skill) => skill.id}
          loading={query.isPending}
          pageSize={25}
          empty={
            <EmptyState
              icon={BookOpenCheck}
              title={skills.length === 0 ? 'No skills yet' : 'No skills match'}
              description={
                skills.length === 0
                  ? 'Skills appear here after internal seeds or reviewed catalog promotions attach procedural memory to agents.'
                  : 'Change the search or category to see more skills.'
              }
            />
          }
        />
      )}
    </ListPage>
  );
};
